<script lang="ts" setup>
const { vuBreakpoints } = useApp()
const { sort, sortOptions, updateSort } = await useCatalogPaginator()

const meta = inject('meta', ref())

const {
  applyOptionsDirectly,
  optionToggle,
  filterRangeReset,
  filterApply,
  filtersApplyAll,
  filtersRemoveAll,
  expectedTotal,
  getExpectedTotal,
  filtersOnQuery,
  activeFiltersOnQuery,
  activeFiltersCountOnQuery,
} = useCatalogFilters(meta)

const {
  open,
  openPanel,
  closePanel,
} = useCatalogFiltersModal()

applyOptionsDirectly.value = true

const filterList = useTemplateRef('filter-list')

onMounted(() => {
  watchEffect(() => {
    applyOptionsDirectly.value = vuBreakpoints.greaterOrEqual('desktop').value
  })
})

function toggleActive(filterKey: string, optionKey: string) {
  optionToggle(filterKey, optionKey)
  filterApply(filterKey)
  getExpectedTotal()
}

function resetRange(filterKey: string) {
  filterRangeReset(filterKey)
  filterApply(filterKey)
  getExpectedTotal()
}

function applyAllAndClose() {
  filtersApplyAll()
  closePanel()
}

function resetAllAndClose() {
  scrollToFilter()
  closePanel()
  filtersRemoveAll()
  filtersApplyAll()
}

function resetAndApplyAll() {
  filtersRemoveAll()
  filtersApplyAll()
}

function scrollToFilter() {
  filterList.value?.scrollIntoView({ behavior: 'auto', block: 'start' })
  filterList.value?.querySelector('.filter-list__scrollable')?.scrollTo({ behavior: 'auto', top: 0 })
}
</script>

<template>
  <div
    ref="filter-list"
    class="filter-list"
  >
    <div class="filter-list__button-container">
      <button
        type="button"
        class="filter-list__toggle"
        @click="openPanel"
      >
        <NuxtIcon name="icon:filter" />
        <span>{{ $t('Filter & Sort') }}</span>
        <small
          v-if="activeFiltersCountOnQuery"
          class="filter-list__count"
        >{{ activeFiltersCountOnQuery }}</small>
      </button>
    </div>

    <div class="filter-list__button-container--sticky">
      <button
        type="button"
        class="filter-list__toggle--sticky"
        @click="openPanel"
      >
        <NuxtIcon name="icon:filter" />
        <span>{{ $t('Filter & Sort') }}</span>
        <small
          v-if="activeFiltersCountOnQuery"
          class="filter-list__count"
        >{{ activeFiltersCountOnQuery }}</small>
      </button>
    </div>

    <div
      :class="{ 'backdrop--active': open }"
      class="backdrop"
      @click="resetAllAndClose"
    />

    <search
      :class="{ 'filter-list__panel--open': open }"
      class="filter-list__panel"
    >
      <div class="filter-list__title">
        <span
          class="filter-list__label"
        >
          {{ $t('Filter and Sort') }}
        </span>
        <button
          type="button"
          class="filter-list__close"
          @click="resetAllAndClose"
        >
          <span>{{ $t('Close') }}</span>
          <NuxtIcon name="icon:close" />
        </button>
      </div>

      <UiScrollable
        class="filter-list__scrollable"
        axis="y"
      >
        <PaginatorToolbar
          :sort="sort"
          :sort-options="sortOptions"
          @update-sort="updateSort"
        />

        <FilterActive
          class="plp__filter-active"
          :active-filters="activeFiltersOnQuery"
          @filters-reset-all="resetAndApplyAll"
          @filter-reset-range="resetRange"
          @option-toggle="toggleActive"
        />

        <FilterItem
          v-for="filter in filtersOnQuery"
          :key="filter.key"
          :filter="filter"
        />
      </UiScrollable>

      <div
        v-if="!applyOptionsDirectly"
        class="filter-list__buttons"
      >
        <button
          type="button"
          class="filter-list__reset-all"
          @click="resetAllAndClose"
        >
          {{ $t('Reset All') }}
        </button>

        <button
          type="button"
          class="filter-list__apply-all"
          :disabled="!expectedTotal"
          @click="scrollToFilter(); applyAllAndClose()"
        >
          <span>{{ $t('Show') }} {{ $tc('No products | {count} product | {count} products', { count: expectedTotal }).toLocaleLowerCase() }}</span>
        </button>
      </div>
    </search>
  </div>
</template>

<style>
@import url("~layers/components/Filter/List.css");
</style>
